import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for stored auth data on mount
    const initAuth = () => {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('user');
      const lastLogin = localStorage.getItem('lastLogin');
      
      if (token && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          // Add lastLogin to user object
          parsedUser.lastLogin = lastLogin || new Date().toISOString();
          setUser(parsedUser);
        } catch (e) {
          console.error('Failed to parse stored user:', e);
          localStorage.removeItem('token');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          localStorage.removeItem('lastLogin');
        }
      }
      
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (emailOrUserData, passwordOrToken, locationData = null) => {
    // Check if this is an API login call (email, password) or direct login (userData, token)
    if (typeof emailOrUserData === 'string' && typeof passwordOrToken === 'string') {
      // This is an email/password login - make API call
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const loginPayload = {
        identifier: emailOrUserData,
        password: passwordOrToken,
        remember_me: false,
      };
      
      // Add location data if provided
      if (locationData) {
        loginPayload.location = locationData;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginPayload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();
      
      if (!data.ok || !data.token) {
        throw new Error(data.message || 'Login failed');
      }

      // Store token and user data
      localStorage.setItem('token', data.token);
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Store last login info - use current time if not provided
      const lastLoginTime = data.lastLogin || new Date().toISOString();
      localStorage.setItem('lastLogin', lastLoginTime);
      
      // Add lastLogin to user object for display
      const userWithLastLogin = {
        ...data.user,
        lastLogin: lastLoginTime
      };
      
      // Store session start time for auto-logout
      localStorage.setItem('sessionStart', new Date().toISOString());
      
      setUser(userWithLastLogin);
      navigate('/');
      return data;
    } else {
      // Direct login with userData and token
      const userData = emailOrUserData;
      const token = passwordOrToken;
      
      localStorage.setItem('token', token);
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      navigate('/');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  const isAuthenticated = () => {
    // Check both state and localStorage for authentication
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('user');
    
    // If we have token and stored user in localStorage, ensure state is synced
    if (token && storedUser && !user) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (e) {
        console.error('Failed to parse user:', e);
        return false;
      }
    }
    
    return !!token && !!storedUser;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};
